import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { categoriesList } from "@/util/common.util";

export const useVideoMockupsStore = defineStore("videoMockups", () => {
  const data = ref([]);
  const categoryFilter = ref([]);
  const subcategoryFilter = ref([]);
  const categorizedData = ref({});
  const categories = ref([]);
  const subcategories = ref([]);
  const dataLoaded = ref(false);
  const videoMockups = ref([]);
  const activeVideoMockupIndex = ref(0);
  const featuredMockups = ref({});

  const filteredData = computed(() => {
    const categoryFilterFlat = categoryFilter.value.flat();
    const subcategoryFilterFlat = subcategoryFilter.value.flat();

    let filteredCatData = {};
    let filteredResult = [];

    if (!categoryFilterFlat.length) {
      filteredCatData = Object.keys(categorizedData.value).reduce(
        (acc, category) => {
          if (!categorizedData.value[category]) return acc;
          return { ...acc, ...categorizedData.value[category] };
        },
        {},
      );
    } else {
      filteredCatData = categoryFilterFlat.reduce((acc, category) => {
        if (!categorizedData.value[category]) return acc;
        return { ...acc, ...categorizedData.value[category] };
      }, {});
    }

    if (!subcategoryFilterFlat.length) {
      filteredResult = Object.keys(filteredCatData).reduce((acc, sub) => {
        if (!filteredCatData[sub]) return acc;
        return [...acc, ...filteredCatData[sub]];
      }, []);
    } else {
      filteredResult = subcategoryFilterFlat.reduce((acc, sub) => {
        if (!filteredCatData[sub]) return acc;
        return [...acc, ...filteredCatData[sub]];
      }, []);
    }

    return [...new Set(filteredResult)];
  });

  const getSubcategoryWithCategories = computed(() => {
    return Object.keys(categorizedData.value).reduce((acc, category) => {
      return {
        ...acc,
        [category]: Object.keys(categorizedData.value[category]),
      };
    }, {});
  });

  const computedCategorizedData = computed(() => categorizedData.value);
  const getVideoMockupConfig = computed(() => videoMockups.value);
  const getActiveVideoMockupConfig = computed(
    () => videoMockups.value[activeVideoMockupIndex.value],
  );
  const getActiveVideoMockupIndex = computed(
    () => activeVideoMockupIndex.value,
  );

  // Actions
  const fetchData = async (params, skip = 0, limit) => {
    try {
      let mockupData;

      if (import.meta.env.VITE_ENV === "prod") {
        mockupData = (await import(`@/data/dynamic_videoMockupsData.json`))
          .default;
      } else if (import.meta.env.VITE_ENV === "dev") {
        mockupData = (await import(`@/data/dynamic_devVideoMockupsData.json`))
          .default;
      } else {
        mockupData = (await import("@/data/videoMockupsData.json")).default;
      }
      const { category, subcategory } = params || {};
      if (category || subcategory) {
        const filteredData = mockupData.filter((mockup) => {
          if (category && subcategory) {
            return mockup.meta.subcategories.includes(subcategory);
          } else if (category) {
            return mockup.meta.category === category;
          } else {
            return mockup;
          }
        });
        data.value = filteredData.slice(skip, limit);
      } else {
        data.value = mockupData.slice(skip, limit);
      }
      await processData(mockupData.slice(skip, limit));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      dataLoaded.value = true;
    }
  };

  const processData = (data) => {
    return new Promise((resolve) => {
      let categorized = data.reduce((acc, curr) => {
        if (curr.meta && curr.meta.category && curr.meta.subcategories) {
          const item = curr;
          if (item?.meta?.types?.includes("featured")) {
            for (const subcat of curr.meta.subcategories) {
              if (!featuredMockups.value[subcat]) {
                featuredMockups.value[subcat] = [];
              }
              featuredMockups.value[subcat].push(item);
            }
          }
          const subcategories = curr.meta.subcategories.reduce(
            (subAcc, sub) => {
              const items = subAcc[sub] || [];

              return { ...subAcc, [sub]: [...items, item] };
            },
            acc[curr.meta.category] || {},
          );

          return { ...acc, [curr.meta.category]: subcategories };
        }
        return acc;
      }, {});

      const reorderCategories = (categories, orderList) => {
        const reordered = {};
        for (const category in orderList) {
          if (orderList?.[category]) {
            reordered[category] = {};
            orderList[category].forEach((sub) => {
              if (categories[category] && categories[category][sub]) {
                reordered[category][sub] = categories[category][sub];
              }
            });
          }
        }
        categorized = reordered;
      };

      reorderCategories(categorized, categoriesList);
      categorizedData.value = categorized;
      categories.value = Object.keys(categorized);
      subcategories.value = categories.value.flatMap((cat) =>
        Object.keys(categorized[cat]),
      );

      resolve();
    });
  };

  const addCategoryFilter = (filter) => {
    if (!categoryFilter.value.includes(filter)) {
      categoryFilter.value.push(filter);
    }
  };

  const removeCategoryFilter = (filter) => {
    categoryFilter.value = categoryFilter.value.filter((f) => f !== filter);
  };

  const updateCategoryFilter = (filter) => {
    categoryFilter.value = filter ? [filter] : [];
  };

  const resetCategoryFilter = () => {
    categoryFilter.value = [];
  };

  const addSubcategoryFilter = (filter) => {
    if (!subcategoryFilter.value.includes(filter)) {
      subcategoryFilter.value.push(filter);
    }
  };

  const removeSubcategoryFilter = (filter) => {
    subcategoryFilter.value = subcategoryFilter.value.filter(
      (f) => f !== filter,
    );
  };

  const updateSubcategoryFilter = (filter) => {
    subcategoryFilter.value = [filter];
  };

  const resetSubcategoryFilter = () => {
    subcategoryFilter.value = [];
  };

  const getMockupById = (id) => {
    return data.value.find((data) => data.id === id);
  };

  const saveVideoMockupInstance = (id, data) => {
    videoMockups.value[id] = data;
  };

  const getVideoMockupInstance = (id) => {
    return videoMockups.value[id];
  };

  const setCurrentVideoMockupIndex = (id) => {
    activeVideoMockupIndex.value = id;
  };

  const $reset = () => {
    videoMockups.value = [];
  };

  return {
    // State
    data,
    dataLoaded,
    categoryFilter,
    subcategoryFilter,
    categories,
    subcategories,
    featuredMockups,
    // Getters
    filteredData,
    categorizedData: computedCategorizedData,
    getSubcategoryWithCategories,
    getVideoMockupConfig,
    getActiveVideoMockupConfig,
    activeVideoMockupIndex: getActiveVideoMockupIndex,
    // Actions
    fetchData,
    addCategoryFilter,
    removeCategoryFilter,
    updateCategoryFilter,
    resetCategoryFilter,
    addSubcategoryFilter,
    removeSubcategoryFilter,
    updateSubcategoryFilter,
    resetSubcategoryFilter,
    getMockupById,
    saveVideoMockupInstance,
    getVideoMockupInstance,
    setCurrentVideoMockupIndex,
    $reset,
  };
});
